/** Factories for project objects, built from the defaults exported by server/studio/schema.py. */

import D from "./project.defaults.json";
import type { AudioClip, CameraKey, Element, Marker, Project, Scene } from "./project.gen";

export const DEFAULTS = D as unknown as {
  project: Project;
  scene: Scene;
  element: Element;
  audioClip: AudioClip;
  cameraKey: CameraKey;
  marker: Marker;
  [k: string]: unknown;
};

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> | T[K] : T[K] };

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Deep merge for plain objects; arrays and scalars in `over` replace. Never mutates. */
export function merge<T>(base: T, over: DeepPartial<T> | undefined): T {
  if (!over) return structuredClone(base);
  const out: Record<string, unknown> = structuredClone(base) as Record<string, unknown>;
  for (const [k, v] of Object.entries(over)) {
    if (v === undefined) continue;
    out[k] = isObj(v) && isObj(out[k]) ? merge(out[k], v as never) : structuredClone(v);
  }
  return out as T;
}

export const makeProject = (o: DeepPartial<Project> = {}): Project => merge(DEFAULTS.project, o);
export const makeScene = (o: DeepPartial<Scene>): Scene => merge(DEFAULTS.scene, o);
export const makeElement = (o: DeepPartial<Element>): Element => merge(DEFAULTS.element, o);
export const makeAudioClip = (o: DeepPartial<AudioClip>): AudioClip => merge(DEFAULTS.audioClip, o);
export const makeCameraKey = (o: DeepPartial<CameraKey>): CameraKey => merge(DEFAULTS.cameraKey, o);
